// frontend/src/utils/apiErrors.js
//
// Turns a failed API response into a sentence a person can read.
//
// The backend speaks two shapes, because the route guard was added on top of
// handlers that predate it:
//
//   legacy   { error: "Failed to update postpaid status." }   ← human text
//   guard    { error: "insufficient_permissions",
//              message: "Your account has read-only access…" }
//
// Components used to render `data.error` directly, which was right for the
// first shape and produced a bare `insufficient_permissions` for the second.
// Telling them apart is easy: a machine code has no spaces.
//
// Translations are keyed by the code (`error_insufficient_permissions` in
// translations.js), so the guard's English fallback is only used for codes
// nobody has translated yet.

const looksLikeCode = (value) =>
    typeof value === 'string' && value.length > 0 && !/\s/.test(value);

// The active translations object, for the callers that are not components and
// so have no `t` prop — today that is useApi, which raises the "your access
// changed" toast on a 403. App.js keeps this in step with its `language` state.
// A module-level value rather than a prop because `language` lives in React
// state, not localStorage, so a plain module cannot look it up.
let activeTranslations = null;

/** Called by App.js whenever the language changes. */
export const setActiveTranslations = (t) => {
    activeTranslations = t;
};

/** The active translations, or null before App.js has mounted. */
export const activeT = () => activeTranslations;

/**
 * @param source an Error thrown by useApi's apiRequest (carries `.data`), or a
 *               parsed response body straight from a raw fetch.
 * @param t      the active translations object.
 * @param fallback shown when the response says nothing usable.
 */
export const errorText = (source, t, fallback = 'Something went wrong.') => {
    const data = (source && source.data) || source || {};
    const code = typeof data.error === 'string' ? data.error : '';

    if (looksLikeCode(code)) {
        return t?.[`error_${code}`] || data.message || fallback;
    }

    // Legacy shape: `error` is already the sentence.
    return code || data.message || source?.message || fallback;
};

/** The machine-readable code, for branching. '' when there is none. */
export const errorCode = (source) => {
    const data = (source && source.data) || source || {};
    return looksLikeCode(data.error) ? data.error : '';
};
